// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas for the VIEW + SPACE family (CC-2c sub-arm 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to author
// LAYOUT / VIEWPORT / SPACE-CONTEXT state — the view+space slice of ТЗ §10.1.
// As with every other domain, each schema here is a plain DATA contract — it
// does NOT mutate anything. Translation to internal `EditorCommand`s happens in
// `viewSpaceDispatcher.ts`, and the reducer pipeline (the same path the UI /
// command line dispatch through) is the only place that ever writes to the
// document. This enforces the CLAUDE.md rules:
//   "Model/Layout/Viewport — это часть модели, а не только UI." +
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур."
//
// SCOPE — the view/space commands that are BOTH `aiCallable: true` in the
// command-registry AND view/space-domain per ТЗ §10.1, MINUS the arms owned by
// other CC-2c sub-arms:
//   • Included (this sub-arm) — every aiCallable:true descriptor in the
//     `layout` category (command-registry/commands/layout.ts):
//       CREATE_VIEWPORT       (MVIEW)  → geometry/create-viewport
//       SWITCH_TO_MODEL_SPACE (MSPACE) → shell/set-layout-context {viewport}
//       SWITCH_TO_PAPER_SPACE (PSPACE) → shell/set-layout-context {paper}
//       SET_LAYOUT            (LAYOUT) → shell/activate-space {paper}
//       RENAME_LAYOUT         (LAYOUT) → shell/rename-space
//   • Excluded — NOT a registry descriptor (App.tsx-local handlers only; would
//     fail the CC-2a guard because the registry has nothing to target, and not
//     reachable by the AI safe pipeline which can only emit registry commands):
//       ZOOM, PAN  — ТЗ §10.1 marks them AI=yes, but NO CommandDescriptor
//                    exists today (see system.viewNav.test.ts gap markers +
//                    docs/test-gaps/cmd-zoom.md / cmd-pan.md). Wiring them is a
//                    separate registry task; emitting them here would be a
//                    placeholder against an absent target (forbidden).
//   • Excluded — NOT a registry descriptor (test-gap, absent today):
//       VPORTS  — exists only as the MVIEW alias "-VPORTS"; no standalone
//                 descriptor (the rectangular-viewport surface IS MVIEW).
//       VPLAYER, VPCLIP — no CommandDescriptor (layout.test.ts gap markers +
//                 docs/test-gaps/cmd-vplayer.md / cmd-vpclip.md).
//   • Excluded — display-refresh only, registry-mandated aiCallable:false:
//       REGEN / REGENALL — ТЗ marks them NOT AI-callable.
//   • Scoped within LAYOUT — the SET (activate-by-name) and RENAME sub-commands
//     are wired to safe-contour EditorCommands: SET_LAYOUT → `shell/activate-space`
//     (the canonical activation command the tab strip + MSPACE/PSPACE also use),
//     RENAME_LAYOUT → `shell/rename-space`. LAYOUT's remaining sub-commands
//     (NEW / DELETE / TEMPLATE / SAVEAS / ?) have NO EditorCommand contract yet
//     (gap specs: docs/test-gaps/cmd-layout.md, editorStore.layoutNew/Delete.test.ts).
//     Modelling them would mean emitting a command with no reducer target — a
//     placeholder, forbidden by ПРИОРИТЕТ #1. They are therefore out of scope
//     until their EditorCommands land (mirrors WIPEOUT's exclusion in sub-arm 1).
//
// Design notes (mirror drawModifySchemas.ts / entitySchemas.ts):
//   - Every action carries a literal `action: "..."` discriminator so the
//     top-level `viewSpaceAiCommandSchema` is a Zod discriminated union, and the
//     action set stays DISJOINT from the block / entity / draw-modify domains
//     (enables `aiDispatcher.ts` to pick the owning domain by `action` alone).
//   - Point objects reuse `aiPoint2DSchema` from blockSchemas ({ x, y }) — the
//     exact shape the `geometry/create-viewport` contract uses — so there is one
//     point-contract truth across the package (no duplicated 2D point schema).
//   - MSPACE / PSPACE switch the layout CONTEXT (paper ↔ floating model space
//     inside a viewport) of the CURRENTLY active layout; the underlying
//     `shell/set-layout-context` EditorCommand carries ONLY a `layoutContext`
//     value, so these two AI commands need no params beyond the discriminator
//     (full-fidelity to the contract, which has no other fields).

import { z } from "zod";

import { parseColorCommandValueStrict } from "@dxf-ai-editor/drawing-model";

import { aiPoint2DSchema } from "./blockSchemas";

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_VIEWPORT (MVIEW) — create a rectangular viewport on a paper layout.
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `geometry/create-viewport` (contracts/src/editor.ts). `spaceId` is the
// paper-layout space the viewport is created in (REQUIRED by the contract — a
// viewport always belongs to a specific layout). `corner1`/`corner2` are the two
// opposite rectangle corners in paper coordinates. `modelExtent` is the optional
// model-bounds box used for MVIEW FIT auto-fit; when omitted the reducer falls
// back to viewHeight=100, center=(0,0) (contract parity).
export const viewportModelExtentSchema = z.object({
  minX: z.number(),
  minY: z.number(),
  maxX: z.number(),
  maxY: z.number(),
});
export type ViewportModelExtent = z.infer<typeof viewportModelExtentSchema>;

export const createViewportAiSchema = z.object({
  action: z.literal("CREATE_VIEWPORT"),
  spaceId: z.string().min(1),
  corner1: aiPoint2DSchema,
  corner2: aiPoint2DSchema,
  modelExtent: viewportModelExtentSchema.optional(),
});
export type CreateViewportAiCommand = z.infer<typeof createViewportAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SWITCH_TO_MODEL_SPACE (MSPACE) — enter floating model space in a viewport.
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD MSPACE shifts input focus INTO a viewport on the active layout so
// subsequent edits operate on model-space entities. Maps to
// `shell/set-layout-context` with `layoutContext: "viewport"`. No params beyond
// the discriminator — the command acts on the currently active layout.
export const switchToModelSpaceAiSchema = z.object({
  action: z.literal("SWITCH_TO_MODEL_SPACE"),
});
export type SwitchToModelSpaceAiCommand = z.infer<
  typeof switchToModelSpaceAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// SWITCH_TO_PAPER_SPACE (PSPACE) — return to paper context (inverse of MSPACE).
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD PSPACE shifts focus from inside a viewport BACK to paper coordinates.
// Maps to `shell/set-layout-context` with `layoutContext: "paper"`. No params
// beyond the discriminator.
export const switchToPaperSpaceAiSchema = z.object({
  action: z.literal("SWITCH_TO_PAPER_SPACE"),
});
export type SwitchToPaperSpaceAiCommand = z.infer<
  typeof switchToPaperSpaceAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// RENAME_LAYOUT (LAYOUT R / LAYOUT RENAME) — rename a layout tab.
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to `shell/rename-space` ({ spaceId, newName }). The AI references the
// layout by its space id (the contract renames by id; the reducer rejects
// renaming the system Model space, duplicate names and empty names — AutoCAD
// parity). `newName` mirrors the contract's 1..255-char bound exactly.
export const renameLayoutAiSchema = z.object({
  action: z.literal("RENAME_LAYOUT"),
  spaceId: z.string().min(1),
  newName: z.string().min(1).max(255),
});
export type RenameLayoutAiCommand = z.infer<typeof renameLayoutAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SET_LAYOUT (LAYOUT SET / LAYOUT S) — make a layout tab the current space.
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's LAYOUT `Set` option makes a paper-space layout the current/active
// space. Maps to `shell/activate-space` ({ spaceId, layoutContext: "paper" }) —
// the SAME canonical activation command the UI tab strip dispatches (single
// source of truth for `activeSpaceId` + `layoutContext`). The AI references the
// layout by its space id (the contract activates by id; the CLI name→id lookup
// is a command-line convenience). LAYOUT Set operates on PAPER-space layouts
// only — the Model tab is not selectable — so the dispatcher fails closed on a
// model / orphan spaceId when the document layout list is supplied.
export const setLayoutAiSchema = z.object({
  action: z.literal("SET_LAYOUT"),
  spaceId: z.string().min(1),
});
export type SetLayoutAiCommand = z.infer<typeof setLayoutAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SET_CURRENT_COLOR (COLOR / -COLOR / SETCOLOR / ЦВЕТ) — set CECOLOR.
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's COLOR command sets the CURRENT colour ($CECOLOR) — the colour that
// NEWLY-created entities inherit. It is a SESSION/document setting, NOT geometry
// creation/modification, so it belongs in this view+space (session-state) domain
// alongside SET_LAYOUT. Maps to `header/set-cecolor` ({ color }). `color` is a
// STRING STRICTLY validated against the AutoCAD COLOR-prompt accept-list
// (ByLayer / ByBlock / 0 / 256 / ACI 1..255 / #rrggbb / `R,G,B` / Truecolor
// keyword) via the ONE-truth `parseColorCommandValueStrict` — the SAME strict
// validator the human COLOR command enforces (CECOLOR FINDING 2), NOT the
// permissive `coerceColor` migration codec. So the AI cannot store an
// out-of-range `$CECOLOR` (`"999"` → invalid `62=999` on export) or silently
// reset to ByLayer on an unknown keyword (`"blu"`): the schema REJECTS the
// command at parse time (defense-in-depth alongside the translator's fail-closed
// `resolveAiCurrentColor`). Per the CLAUDE.md AI rule the dispatcher EMITS a
// command (it never writes the document directly).
export const setCurrentColorAiSchema = z.object({
  action: z.literal("SET_CURRENT_COLOR"),
  color: z.string().refine((v) => parseColorCommandValueStrict(v) !== null, {
    message:
      'Invalid colour. Accepted: ByLayer / ByBlock / ACI 1..255 / #rrggbb / "R,G,B".',
  }),
});
export type SetCurrentColorAiCommand = z.infer<typeof setCurrentColorAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Top-level view+space AI command discriminated union
// ═══════════════════════════════════════════════════════════════════════════
//
// 6 view+space AI schemas:
//   Viewport: CREATE_VIEWPORT (MVIEW)
//   Context:  SWITCH_TO_MODEL_SPACE (MSPACE), SWITCH_TO_PAPER_SPACE (PSPACE)
//   Layout:   SET_LAYOUT (LAYOUT SET), RENAME_LAYOUT (LAYOUT R)
//   Colour:   SET_CURRENT_COLOR (COLOR — CECOLOR)
export const viewSpaceAiCommandSchema = z.discriminatedUnion("action", [
  createViewportAiSchema,
  switchToModelSpaceAiSchema,
  switchToPaperSpaceAiSchema,
  setLayoutAiSchema,
  renameLayoutAiSchema,
  setCurrentColorAiSchema,
]);
export type ViewSpaceAiCommand = z.infer<typeof viewSpaceAiCommandSchema>;

// A batch of view+space commands the AI can emit in one response. The
// dispatcher resolves each in order.
export const viewSpaceAiCommandBatchSchema = z.array(viewSpaceAiCommandSchema);
export type ViewSpaceAiCommandBatch = z.infer<
  typeof viewSpaceAiCommandBatchSchema
>;

// Convenience: literal action names for the router, guard map, error messages
// and telemetry. Kept in sync with the union by the exhaustive guard Record.
export const VIEW_SPACE_AI_ACTIONS = [
  "CREATE_VIEWPORT",
  "SWITCH_TO_MODEL_SPACE",
  "SWITCH_TO_PAPER_SPACE",
  "SET_LAYOUT",
  "RENAME_LAYOUT",
  "SET_CURRENT_COLOR",
] as const;
export type ViewSpaceAiAction = (typeof VIEW_SPACE_AI_ACTIONS)[number];

const VIEW_SPACE_ACTION_SET: ReadonlySet<string> = new Set(VIEW_SPACE_AI_ACTIONS);

/**
 * Router predicate: does this AI command belong to the view+space domain?
 *
 * The view+space, block, entity, and draw-modify action literal sets are
 * DISJOINT, so a membership test on `action` is a sound discriminator. The type
 * predicate lets `aiDispatcher.ts` narrow the top-level union to the owning
 * domain without importing the `AiCommand` type here (which would create a
 * module cycle).
 */
export function isViewSpaceAiCommand(
  command: { action: string },
): command is ViewSpaceAiCommand {
  return VIEW_SPACE_ACTION_SET.has(command.action);
}
